import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target, CalendarRange, Users, Coins, ShieldAlert, Handshake, Globe2, FileText, Rocket, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import CommunityCard from "@/components/CommunityCard";
import DocumentActions from "@/components/planner/DocumentActions";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const DOC_LABELS = {
  surat_izin: "Surat Izin RT/RW/Kelurahan",
  surat_kolaborasi: "Surat Kolaborasi",
  proposal: "Project Proposal",
  mou: "CSR MoU",
};

function Section({ no, icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="font-heading font-extrabold text-xl text-slate-900 mb-4 flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white font-black text-xs flex items-center justify-center shrink-0">{no}</span>
        <Icon className="w-5 h-5 text-emerald-600" /> {title}
      </h3>
      {children}
    </div>
  );
}

export default function PlaybookV2({ result, region, volunteers }) {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [converting, setConverting] = useState(false);
  const docs = result.documents || {};
  const docKeys = Object.keys(docs).filter((k) => docs[k]);

  const convert = async () => {
    setConverting(true);
    try {
      const capacity = parseInt(volunteers || "0", 10) ||
        (result.roles || []).reduce((s, r) => s + (parseInt(r.count, 10) || 0), 0) || 15;
      await api.post("/events", {
        title: result.title,
        category: result.category,
        capacity,
        city: ["Jakarta", "Bandung", "Surabaya", "Yogyakarta", "Bekasi"].find((c) => (region || "").toLowerCase().includes(c.toLowerCase())) || "Jakarta",
        address: region || "",
        description: `${result.summary} Target: ${(result.analysis?.goals || []).join("; ")}`,
        skills: (result.roles || []).slice(0, 3).map((r) => r.role),
        organizer: user?.name || "Inisiatif Warga",
      });
      toast.success(t("convert_success"));
      navigate("/jelajahi");
    } catch {
      toast.error(t("convert_error"));
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="mt-10 space-y-10" data-testid="playbook-result">
      <div className="bg-slate-900 rounded-2xl p-8">
        <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">{result.category} · {result.actor}</span>
        <h2 data-testid="playbook-title" className="font-heading font-black text-2xl md:text-3xl text-white tracking-tight mt-2">{result.title}</h2>
        <p className="text-slate-300 mt-3 leading-relaxed">{result.summary}</p>
      </div>

      <Section no={1} icon={Target} title={t("pb_s1")} testid="pb-analysis">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs font-black text-slate-400 uppercase mb-1">{t("pb_root")}</p>
            <p className="text-sm text-slate-700">{result.analysis?.root_cause}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs font-black text-slate-400 uppercase mb-1">{t("pb_benef")}</p>
            <p className="text-sm text-slate-700">{result.analysis?.beneficiaries}</p>
          </div>
          <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5">
            <p className="text-xs font-black text-emerald-700 uppercase mb-1">{t("pb_goals")}</p>
            <ul className="space-y-1">{(result.analysis?.goals || []).map((g, i) => <li key={i} className="text-sm text-emerald-800">• {g}</li>)}</ul>
          </div>
        </div>
      </Section>

      <Section no={2} icon={CalendarRange} title={t("pb_s2")} testid="pb-timeline">
        <div className="grid md:grid-cols-3 gap-4">
          {(result.timeline || []).map((ph, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5">
              <h4 className="font-heading font-extrabold text-sm text-slate-900 mb-3">{ph.name}</h4>
              <ul className="space-y-1.5">{(ph.steps || []).map((s, j) => <li key={j} className="text-sm text-slate-600 flex gap-2"><span className="text-emerald-500 font-black">•</span>{s}</li>)}</ul>
            </div>
          ))}
        </div>
      </Section>

      <Section no={3} icon={Users} title={t("pb_s3")} testid="pb-roles">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {(result.roles || []).map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100 last:border-0">
              <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 font-black text-sm flex items-center justify-center shrink-0">{r.count}×</span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold text-sm text-slate-900">{r.role}</p>
                <p className="text-xs text-slate-500">{r.responsibility}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section no={4} icon={Coins} title={t("pb_s4")} testid="pb-budget">
        <div className="grid md:grid-cols-3 gap-4">
          {(result.budget_scenarios || []).map((sc, i) => (
            <div key={i} className={`rounded-2xl border p-5 ${i === 1 ? "bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200" : "bg-white border-slate-200"}`}>
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-extrabold text-sm text-slate-900">{sc.name}</h4>
                {i === 1 && <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-600 text-white">{t("pb_reco")}</span>}
              </div>
              <p className="font-heading font-black text-2xl text-slate-900 mt-2">{fmtRp(sc.total)}</p>
              <ul className="mt-3 space-y-1">
                {(sc.items || []).map((it, j) => (
                  <li key={j} className="text-xs text-slate-600 flex justify-between gap-2"><span>{it.item}</span><span className="font-bold shrink-0">{fmtRp(it.cost)}</span></li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-400 mt-3">{sc.note}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> {result.price_note}</p>
      </Section>

      <Section no={5} icon={ShieldAlert} title={t("pb_s5")} testid="pb-risks">
        <div className="grid md:grid-cols-2 gap-4">
          {(result.risks || []).map((r, i) => (
            <div key={i} className="bg-rose-50 rounded-2xl border border-rose-200 p-5">
              <p className="text-sm font-bold text-rose-800">⚠ {r.risk}</p>
              <p className="text-sm text-rose-700 mt-1">→ {r.mitigation}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section no={6} icon={Handshake} title={t("pb_s6")} testid="pb-partners">
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          {(result.partners_reco || []).map((p, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5">
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-900 text-white">{p.type}</span>
              <p className="font-bold text-sm text-slate-900 mt-2">{p.name}</p>
              <p className="text-xs text-slate-500 mt-1">{p.role}</p>
            </div>
          ))}
        </div>
        {result.partners?.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-directory">
            {result.partners.map((p) => <CommunityCard key={p.id} community={p} />)}
          </div>
        )}
      </Section>

      <Section no={7} icon={Globe2} title={t("pb_s7")} testid="pb-sdg">
        <div className="grid md:grid-cols-2 gap-4">
          {(result.sdg || []).map((s, i) => (
            <div key={i} className="bg-slate-900 rounded-2xl p-5">
              <span className="text-xs font-black px-2.5 py-1 rounded-lg bg-emerald-600 text-white">{s.code}</span>
              <p className="font-heading font-extrabold text-white mt-2">{s.name}</p>
              <p className="text-xs text-slate-300 mt-1">{s.rationale}</p>
              <ul className="mt-2 space-y-1">{(s.kpis || []).map((k, j) => <li key={j} className="text-xs text-emerald-300">📊 {k}</li>)}</ul>
            </div>
          ))}
        </div>
      </Section>

      <Section no={8} icon={FileText} title={t("pb_s8")} testid="pb-docs">
        <Tabs defaultValue={docKeys[0]}>
          <TabsList className="rounded-full bg-slate-100 p-1 flex-wrap h-auto">
            {docKeys.map((k) => (
              <TabsTrigger key={k} data-testid={`doc-tab-${k}`} value={k} className="rounded-full font-bold data-[state=active]:bg-white">{DOC_LABELS[k] || k}</TabsTrigger>
            ))}
          </TabsList>
          {docKeys.map((k) => (
            <TabsContent key={k} value={k} className="mt-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div id="planner-document">
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">{docs[k]}</pre>
                </div>
                <DocumentActions text={docs[k]} summary={`${result.title} — ${DOC_LABELS[k] || k}`} />
              </div>
            </TabsContent>
          ))}
        </Tabs>
        <p className="text-xs text-slate-400 mt-3 italic">{result.disclaimer}</p>
      </Section>

      <div data-testid="convert-card" className="bg-emerald-600 rounded-2xl p-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-heading font-black text-xl text-white">{t("convert_ready")}</h3>
          <p className="text-emerald-100 text-sm mt-1">{t("convert_desc")}</p>
        </div>
        <Button
          data-testid="convert-project-btn"
          onClick={convert}
          disabled={converting}
          className="rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-black px-7 h-12"
        >
          <Rocket className="w-5 h-5 mr-2" /> {converting ? "Memublikasikan..." : t("convert_btn")}
        </Button>
      </div>
    </div>
  );
}
